/**
 * Unified handle URL builder. Source of truth for every dancer /
 * organization URL the app emits.
 *
 * Three variants per entity type:
 *
 *   *Path(handle)         '/<h>'                              — internal Link / redirect / router.push / revalidatePath
 *   *ShortUrl(handle)     'https://ballee.co/<h>'             — user-facing copy: emails, share buttons, OG text
 *   *CanonicalUrl(handle) 'https://ballee.co/<h>'             — SEO link rel=canonical, og:url, sitemap loc
 *
 * The path / short / canonical variants now all resolve to the same
 * short form (`/<h>` and `https://ballee.co/<h>`). The legacy
 * `/dancers/<h>` and `/organizations/<h>` paths are still served — but
 * only as permanent 301 redirects to the short form. See
 * `apps/web/app/(adaptive)/dancers/[slug]/page.tsx` and
 * `apps/web/app/(adaptive)/organizations/[username]/page.tsx`. Use
 * `dancerLongPath()` / `organizationLongPath()` ONLY in those redirect
 * routes, never for new code.
 *
 * Migration date: 2026-05-24. Driver: GSC was indexing the long form
 * because canonical + sitemap declared it; user confirmed only short
 * URLs are publicly advertised, so the canonical was flipped to match.
 *
 * The apex (`ballee.co`) is hardcoded here, NOT read from
 * BRAND_CONFIG. Two reasons:
 *   1. Dancer + organization profiles are a Ballee-apex-only surface per
 *      `docs/architecture/domain-and-seo-architecture.md`. Canonicals
 *      MUST always point to `ballee.co`, even when rendered from Flow
 *      or a Vercel preview host.
 *   2. This module lives in `@kit/shared` so packages can consume it;
 *      `~/lib/brand` is an apps/web-scoped alias and is not importable
 *      from packages.
 *
 * Enforcement: `eslint-plugin-handles/no-hardcoded-handle-url` blocks
 * raw `/dancers/${...}` and `/organizations/${...}` literals outside
 * this file and a tiny allowlist (the resolver + proxy + tests).
 */

const APEX = 'https://ballee.co';

export type HandleEntityType = 'dancer' | 'organization';

/**
 * Inputs are typed as nullable because many call sites read the handle
 * off an optional join (e.g. `author.username`) where the column is
 * nullable. Producing `/null` matches the pre-utility behaviour
 * of the inlined template literals — these broken links existed before
 * this refactor and fixing them is out of scope. Callers that want to
 * skip rendering a link entirely when the handle is missing should
 * guard at the call site.
 */
type Handle = string | null | undefined;

// --- Internal canonical paths (host-relative) ----------------------------
//
// Internal navigation goes through these. They return the short form so
// that <Link href={dancerPath(h)}> resolves directly without hitting the
// legacy 301 redirect.

export function dancerPath(handle: Handle): string {
  return `/${handle}`;
}

export function organizationPath(handle: Handle): string {
  return `/${handle}`;
}

// The single universal org-editor entry. Its page
// (`home/(user)/community/organizations/[username]/edit/page.tsx`) handles the
// account_id branch (account-linked orgs 308-redirect to the team-account
// editor; account-less orgs render the membership-scoped editor). Public-page
// Edit buttons link here so they never need to replicate that branching.
export function organizationEditPath(handle: Handle): string {
  return `/home/community/organizations/${handle}/edit`;
}

// --- Legacy long-form paths (for 301 redirect routes only) ---------------
//
// ONLY for use by the redirect handlers at
// `apps/web/app/(adaptive)/dancers/[slug]/page.tsx` and
// `apps/web/app/(adaptive)/organizations/[username]/page.tsx` to
// construct or recognize their own URL. Every other caller MUST use
// `dancerPath()` / `organizationPath()` which now return the short
// form. The eslint rule `eslint-plugin-handles/no-hardcoded-handle-url`
// still applies — these helpers are the ONLY way to construct the
// legacy path.

export function dancerLongPath(handle: Handle): string {
  return `/dancers/${handle}`;
}

export function organizationLongPath(handle: Handle): string {
  return `/organizations/${handle}`;
}

// --- Public short URLs (user-facing copy) --------------------------------

export function dancerShortUrl(handle: Handle): string {
  return `${APEX}/${handle}`;
}

export function organizationShortUrl(handle: Handle): string {
  return `${APEX}/${handle}`;
}

// --- Absolute canonical URLs (SEO) ---------------------------------------
//
// Equivalent to the *ShortUrl helpers above. Kept as a separate name so
// that call sites communicate intent — using `dancerCanonicalUrl()` in
// a `<link rel="canonical">` makes the reason for the URL choice
// explicit, even though the value is identical to `dancerShortUrl()`.

export function dancerCanonicalUrl(handle: Handle): string {
  return `${APEX}/${handle}`;
}

export function organizationCanonicalUrl(handle: Handle): string {
  return `${APEX}/${handle}`;
}

// --- Marketplace sub-page URLs -------------------------------------------
//
// A dancer's public marketplace items live at `<handle>/marketplace` — a
// shareable, social-media-friendly URL listing everything that dancer has
// for sale. Rendered by `apps/web/app/[handle]/marketplace/page.tsx`.
// Hardcoded to apex for the same reason as the per-entity canonicals above.

export function dancerMarketplacePath(handle: Handle): string {
  return `/${handle}/marketplace`;
}

export function dancerMarketplaceShortUrl(handle: Handle): string {
  return `${APEX}/${handle}/marketplace`;
}

export function dancerMarketplaceCanonicalUrl(handle: Handle): string {
  return `${APEX}/${handle}/marketplace`;
}

// A seller's marketplace profile — the always-public aggregation of every
// active item they have for sale — lives at `/marketplace/seller/<sellerId>`,
// keyed on `marketplace_listings.seller_id` (the auth user id). Unlike the
// dancer marketplace page above, this is a distinct entity from the dancer
// profile: it exists and is public the moment a user has a listing, regardless
// of whether their dancer profile is private (or whether they're a dancer at
// all). Rendered by `apps/web/app/(adaptive)/marketplace/seller/[sellerId]/page.tsx`.
export function marketplaceSellerPath(sellerId: string): string {
  return `/marketplace/seller/${sellerId}`;
}

// --- Audition / competition public pages ---------------------------------
//
// Auditions and competitions are the same `auditions` row; the public
// surface splits on `is_competition`. Both routes are keyed by slug:
// `apps/web/app/(adaptive)/auditions/[slug]/page.tsx` and
// `apps/web/app/(adaptive)/competitions/[slug]/page.tsx`. Callers that
// have the `is_competition` flag pick the matching helper.

export function auditionPath(slug: Handle): string {
  return `/auditions/${slug}`;
}

export function competitionPath(slug: Handle): string {
  return `/competitions/${slug}`;
}

// --- Community event public pages ----------------------------------------
//
// A `community_events` row surfaces publicly at `/events/<slug>` (only when
// `published = true`). Keyed by slug like the audition / competition paths;
// `apps/web/app/(adaptive)/events/[slug]/page.tsx`.

export function eventPath(slug: Handle): string {
  return `/events/${slug}`;
}

// --- Browse-index URLs (SEO + fallback for empty result lists) -----------
//
// The `/dancers` and `/organizations` browse pages use these as the
// `alternates.canonical` and as the fallback for ItemList entries when an
// individual row has no handle. These are listing pages, NOT entity
// profiles, so they keep their typed paths. Hardcoded to apex for the
// same reason as the per-entity canonicals above.

export const dancersIndexUrl = `${APEX}/dancers`;
export const organizationsIndexUrl = `${APEX}/organizations`;

// --- Generic apex canonical for listing / facet / directory pages --------
//
// The faceted directory, browse-by-letter, city, and companies listing
// pages build dynamic canonical paths that don't map to a single typed
// helper above. They MUST still canonicalize to the apex, never to the
// request host — deriving from `appConfig.url` (which is
// `NEXT_PUBLIC_SITE_URL=https://flow.ballee.co` in production) emits a
// cross-host canonical that bleeds ranking signal off ballee.co onto the
// Flow app domain. This is the single sanctioned way for a page-level
// `alternates.canonical` to turn a host-relative path into an apex URL.
// See `feedback_canonical_hardcode_apex_on_multi_host`.
export function apexCanonicalUrl(path: string): string {
  return new URL(path, APEX).href;
}
